/**
 * Roadmap Editor
 *
 * Full-featured editor for a single Roadmap document. Supports
 * four view modes: Tree, Grid, Split (tree + details), Canvas.
 * Node detail panel opens on the right when a node is selected.
 *
 * Route: /prioritization/roadmap/:id
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import {
  ArrowLeft,
  Loader2,
  Network,
  Grid3X3,
  Columns2,
  ChevronRight,
  ChevronDown,
  Trash2,
  Plus,
  Check,
  Rows3,
  GripVertical,
  Pencil,
  X,
  HelpCircle,
  Flag,
  Circle,
  ArrowRight,
  Clock,
  Calendar,
  Target,
  Layers,
  Link2,
  Printer,
  AlertTriangle,
  MapPin,
  Share2,
} from 'lucide-react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  arrayMove,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { LinkToItemPanel } from '../../components/methodology/LinkToItemPanel';
import {
  getConnectionsForSource,
  deleteConnection,
  type MethodologyConnection,
} from '../../lib/methodologyConnectionsService';
import { createWorkspaceItemFromPlanCondition } from '../../lib/createWorkspaceItemFromPlanCondition';
import { useItemsStore } from '../../stores/prioritization/itemsStore';
import { TreeView } from '../../components/roadmap/TreeView';
import { GridView } from '../../components/roadmap/GridView';
import { useRoadmapStore } from '../../stores/prioritization/roadmapStore';
import { RoadmapMiroModal } from '../../components/roadmap/RoadmapMiroModal';
import { AppSelect } from '../../components/ui/AppSelect';
import { EntitySyncPanel } from '../../components/ui/EntitySyncPanel';
import { StrategyConnectivityHelpButton } from '../prioritization/strategy/StrategyConnectivityDocModal';
import { CustomFieldsSection } from '../../components/prioritization/item-view/CustomFieldsSection';
import type {
  RoadmapViewMode,
  RoadmapNode,
  RoadmapSwimlane,
  RoadmapMilestoneColumn,
} from '../../stores/prioritization/roadmapStore';

// ── View mode tabs ────────────────────────────────────────────

const VIEW_MODES: { id: RoadmapViewMode; label: string; icon: React.ReactNode }[] = [
  { id: 'Tree', label: 'Tree', icon: <Network className="h-3.5 w-3.5" /> },
  { id: 'Grid', label: 'Grid', icon: <Grid3X3 className="h-3.5 w-3.5" /> },
  { id: 'Split', label: 'Split', icon: <Columns2 className="h-3.5 w-3.5" /> },
];

// ── Node detail panel ─────────────────────────────────────────

interface NodeDetailPanelProps {
  node: RoadmapNode;
  onClose: () => void;
}

function NodeDetailPanel({ node, onClose }: NodeDetailPanelProps) {
  const { updateNode, deleteNode } = useRoadmapStore();

  const [editingText, setEditingText] = useState(false);
  const [textDraft, setTextDraft] = useState(node.text);
  const [isSavingText, setIsSavingText] = useState(false);

  const [status, setStatus] = useState(node.status);
  const [isSavingStatus, setIsSavingStatus] = useState(false);

  const [connections, setConnections] = useState<MethodologyConnection[]>([]);
  const [loadingConnections, setLoadingConnections] = useState(false);
  const [showLink, setShowLink] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [wsItemSaving, setWsItemSaving] = useState(false);
  const [wsItemUnlinking, setWsItemUnlinking] = useState(false);
  const [wsItemErr, setWsItemErr] = useState('');

  const { items } = useItemsStore();

  const representedWorkspaceItem =
    node.itemId ? items.find((i) => i.id === node.itemId) ?? null : null;

  const nodeId = node.id;
  useEffect(() => {
    setTextDraft(node.text);
    setStatus(node.status);
    setEditingText(false);
    setShowLink(false);
    setWsItemErr('');
    loadConnections();
  }, [nodeId]); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadConnections() {
    setLoadingConnections(true);
    const { data } = await getConnectionsForSource('roadmap_node', node.id);
    setConnections(data);
    setLoadingConnections(false);
  }

  async function handleRemoveConnection(id: string) {
    setRemovingId(id);
    await deleteConnection(id);
    setConnections((prev) => prev.filter((c) => c.id !== id));
    setRemovingId(null);
  }

  function handleStartEdit() {
    setTextDraft(node.text);
    setEditingText(true);
  }

  function handleCancelEdit() {
    setTextDraft(node.text);
    setEditingText(false);
  }

  async function handleSaveText() {
    if (textDraft.trim() === node.text) { setEditingText(false); return; }
    setIsSavingText(true);
    await updateNode(node.id, { text: textDraft.trim() });
    setIsSavingText(false);
    setEditingText(false);
  }

  async function handleStatusChange(newStatus: RoadmapNode['status']) {
    setStatus(newStatus);
    setIsSavingStatus(true);
    await updateNode(node.id, { status: newStatus });
    setIsSavingStatus(false);
  }

  async function handleSaveAsWorkspaceItem() {
    const raw = editingText ? textDraft : node.text;
    const t = raw.trim();
    if (!t) return;

    setWsItemErr('');
    setWsItemSaving(true);

    if (editingText && t !== node.text.trim()) {
      const { success, error } = await updateNode(node.id, { text: t });
      if (!success) {
        setWsItemSaving(false);
        setWsItemErr(error ?? 'Could not save condition.');
        return;
      }
      setTextDraft(t);
      setEditingText(false);
    }

    const { itemId, error } = await createWorkspaceItemFromPlanCondition({
      title: t,
      source: 'roadmap_node',
    });
    if (error || !itemId) {
      setWsItemSaving(false);
      setWsItemErr(error ?? 'Could not create item.');
      return;
    }
    const { success, error: uErr } = await updateNode(node.id, { itemId });
    setWsItemSaving(false);
    if (!success) {
      setWsItemErr(uErr ?? 'Failed to link item to node.');
    }
  }

  async function handleUnlinkWorkspaceItem() {
    setWsItemErr('');
    setWsItemUnlinking(true);
    const { success, error } = await updateNode(node.id, { itemId: null });
    setWsItemUnlinking(false);
    if (!success) {
      setWsItemErr(error ?? 'Could not unlink item.');
    }
  }

  async function handleDelete() {
    setIsDeleting(true);
    await deleteNode(node.id);
    onClose();
  }

  const statusOptions = [
    { value: 'unknown', label: 'Unknown' },
    { value: 'true', label: 'True' },
    { value: 'in-progress', label: 'In progress' },
    { value: 'not-true', label: 'Not true' },
  ] as const;

  const effectiveConditionTrim = (editingText ? textDraft : node.text).trim();
  const disableConditionActions = isSavingText || wsItemSaving;
  const disableSaveAsItem =
    !effectiveConditionTrim || !!node.itemId || wsItemSaving || isSavingText;

  return (
    <div className="w-72 flex-shrink-0 border border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] p-4 flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-[var(--text-sec)] dark:text-[var(--text-sec)]">Node detail</h3>
        <button
          onClick={onClose}
          className="p-1 rounded hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)] text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:hover:text-[var(--text-ter)]"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      {/* ── Condition text ── */}
      <div>
        <label className="text-xs font-medium text-[var(--text-ter)] dark:text-[var(--text-ter)] block mb-1">
          Condition
        </label>

        {editingText ? (
          <>
            <textarea
              autoFocus
              value={textDraft}
              onChange={(e) => setTextDraft(e.target.value)}
              disabled={disableConditionActions}
              onKeyDown={(e) => {
                if (e.key === 'Escape') handleCancelEdit();
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleSaveText();
              }}
              rows={4}
              className="w-full px-3 py-2 text-sm border border-[var(--slate)] dark:border-[var(--slate)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] text-[var(--text-pri)] dark:text-[var(--text-pri)] focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none disabled:opacity-60"
            />
            <div className="flex flex-wrap items-center gap-2 mt-2">
              <button
                type="button"
                onClick={handleSaveText}
                disabled={disableConditionActions}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-[var(--slate)] hover:bg-[var(--slate)] disabled:opacity-60 text-white text-xs font-medium transition-colors"
              >
                {isSavingText ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <Check className="h-3 w-3" />
                )}
                Save
              </button>
              <button
                type="button"
                onClick={handleCancelEdit}
                disabled={disableConditionActions}
                className="px-3 py-1.5 text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:hover:text-[var(--text-sec)] transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              {!node.itemId && (
                <button
                  type="button"
                  onClick={handleSaveAsWorkspaceItem}
                  disabled={disableSaveAsItem}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors border border-[var(--green)] text-[var(--green)] dark:border-[var(--green)] dark:text-[var(--text-ter)] hover:bg-[var(--green-dim)] dark:hover:bg-[var(--green)]/40 disabled:opacity-45 disabled:pointer-events-none"
                  title={!effectiveConditionTrim ? 'Enter condition text first' : undefined}
                >
                  {wsItemSaving ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <Link2 className="h-3 w-3" />
                  )}
                  Save as workspace item
                </button>
              )}
              <span className="min-w-0 flex-1 text-[10px] text-[var(--text-ter)] text-right whitespace-nowrap sm:whitespace-normal">
                ⌘↵ saves condition
              </span>
            </div>
          </>
        ) : (
          <>
            <button
              type="button"
              onClick={handleStartEdit}
              className="w-full text-left px-3 py-2.5 border border-[var(--rule)] bg-[var(--surface)] dark:bg-[var(--bg-raised)]/40 text-sm text-[var(--text-pri)] dark:text-[var(--text-pri)] hover:border-[var(--slate)] dark:hover:border-[var(--slate)] hover:bg-[var(--slate-dim)]/50 dark:hover:bg-[var(--slate)]/10 transition-colors"
              title="Click to edit"
            >
              <span className={node.text ? '' : 'italic text-[var(--text-ter)]'}>
                {node.text || 'No text — click to add'}
              </span>
            </button>
            <div className="flex flex-wrap items-center gap-2 mt-2">
              <button
                type="button"
                onClick={handleStartEdit}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-[var(--slate)] hover:bg-[var(--slate)] text-white text-xs font-medium transition-colors"
              >
                <Pencil className="h-3 w-3" />
                Edit
              </button>
              {!node.itemId && (
                <button
                  type="button"
                  onClick={handleSaveAsWorkspaceItem}
                  disabled={disableSaveAsItem}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors border border-[var(--green)] text-[var(--green)] dark:border-[var(--green)] dark:text-[var(--text-ter)] hover:bg-[var(--green-dim)] dark:hover:bg-[var(--green)]/40 disabled:opacity-45 disabled:pointer-events-none"
                  title={!effectiveConditionTrim ? 'Enter condition text first' : undefined}
                >
                  {wsItemSaving ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <Link2 className="h-3 w-3" />
                  )}
                  Save as workspace item
                </button>
              )}
            </div>
          </>
        )}

        {wsItemErr ? (
          <p className="mt-2 text-[11px] text-[var(--coral)] dark:text-[var(--coral)]">{wsItemErr}</p>
        ) : null}
      </div>

      {/* ── Status ── */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="text-xs font-medium text-[var(--text-ter)] dark:text-[var(--text-ter)]">
            Status
          </label>
          {isSavingStatus && <Loader2 className="h-3 w-3 animate-spin text-[var(--text-ter)]" />}
        </div>
        <AppSelect
          value={status}
          onChange={(v) => handleStatusChange(v as RoadmapNode['status'])}
          accentColor="violet"
          options={statusOptions}
        />
      </div>

      {/* ── Workspace item (when linked; create from buttons above when not linked) ── */}
      {node.itemId ? (
        <div>
          <label className="text-xs font-medium text-[var(--text-ter)] dark:text-[var(--text-ter)] block mb-1">
            Workspace item
          </label>
          <p className="text-[10px] text-[var(--text-ter)] mb-2 leading-snug">
            Opens the prioritization item that represents this condition. Methodology links below are optional.
          </p>
          <div className="space-y-2">
            <div className="flex items-center gap-2 px-2.5 py-1.5 border border-[var(--green)] dark:border-[var(--green)]/50 bg-[var(--green-dim)]/50 dark:bg-[var(--green)]/25">
              <Link2 className="h-3.5 w-3.5 text-[var(--green)] flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <Link
                  to={`/prioritization/items/${node.itemId}`}
                  className="text-xs font-medium text-[var(--green)] dark:text-[var(--text-ter)] hover:underline truncate block"
                >
                  {representedWorkspaceItem?.title ?? 'Open workspace item'}
                </Link>
              </div>
            </div>
            <Link
              to={`/prioritization/items/${node.itemId}/print`}
              className="inline-flex items-center gap-1 px-2.5 text-[11px] font-medium text-[var(--text-sec)] hover:text-[var(--text-pri)] dark:hover:text-[var(--text-ter)]"
            >
              <Printer className="h-3 w-3 flex-shrink-0" aria-hidden />
              Print-ready view (paper sizing)
            </Link>
            <button
              type="button"
              onClick={handleUnlinkWorkspaceItem}
              disabled={wsItemUnlinking || wsItemSaving}
              className="w-full px-3 py-1.5 text-xs text-[var(--text-sec)] border border-[var(--rule)] hover:bg-[var(--surface)] dark:hover:bg-[var(--bg-raised)]/80 transition-colors disabled:opacity-50"
            >
              {wsItemUnlinking ? (
                <span className="inline-flex items-center gap-1.5">
                  <Loader2 className="h-3 w-3 animate-spin" /> Unlinking…
                </span>
              ) : (
                'Unlink workspace item'
              )}
            </button>
          </div>
        </div>
      ) : null}

      {/* ── Linked items ── */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <div className="flex items-center gap-1.5">
            <span className="text-xs font-medium text-[var(--text-ter)] dark:text-[var(--text-ter)]">
              Linked items
            </span>
            {loadingConnections && <Loader2 className="h-3 w-3 animate-spin text-[var(--text-ter)]" />}
            {connections.length > 0 && (
              <span className="text-[10px] text-[var(--text-ter)]">
                {connections.length}
              </span>
            )}
          </div>
          {!showLink && (
            <button
              onClick={() => setShowLink(true)}
              className="flex items-center gap-1 text-[11px] text-[var(--text-ter)] dark:text-[var(--text-ter)] hover:text-[var(--text-ter)] dark:hover:text-[var(--text-ter)] transition-colors"
            >
              <Plus className="h-3 w-3" />
              Add
            </button>
          )}
        </div>

        {connections.length > 0 && (
          <div className="space-y-1 mb-2">
            {connections.map((conn) => {
              const linkedItem = items.find((i) => i.id === conn.targetEntityId);
              return (
                <div
                  key={conn.id}
                  className="flex items-center gap-2 px-2.5 py-1.5 border border-[var(--slate)] dark:border-[var(--slate)]/60 bg-[var(--slate-dim)]/60 dark:bg-[var(--slate)]/10 group"
                >
                  <Link2 className="h-3 w-3 text-[var(--text-ter)] flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-[var(--text-pri)] dark:text-[var(--text-pri)] truncate font-medium">
                      {linkedItem?.title ?? conn.targetEntityId.slice(0, 12) + '…'}
                    </p>
                    <p className="text-[10px] text-[var(--text-ter)] dark:text-[var(--text-ter)] capitalize">
                      {conn.connectionType.replace('_', ' ')}
                    </p>
                  </div>
                  <button
                    onClick={() => handleRemoveConnection(conn.id)}
                    disabled={removingId === conn.id}
                    className="flex-shrink-0 opacity-0 group-hover:opacity-100 p-0.5 rounded text-[var(--text-ter)] hover:text-[var(--coral)] transition-all disabled:opacity-50"
                    title="Remove link"
                  >
                    {removingId === conn.id
                      ? <Loader2 className="h-3 w-3 animate-spin" />
                      : <X className="h-3 w-3" />
                    }
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {!loadingConnections && connections.length === 0 && !showLink && (
          <button
            onClick={() => setShowLink(true)}
            className="flex items-center gap-1.5 w-full px-3 py-2 text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] border border-dashed border-[var(--slate)] dark:border-[var(--slate)] hover:bg-[var(--slate-dim)] dark:hover:bg-[var(--slate)]/20 transition-colors"
          >
            <Link2 className="h-3.5 w-3.5" />
            Link to a prioritization item
          </button>
        )}

        {showLink && (
          <LinkToItemPanel
            sourceEntityType="roadmap_node"
            sourceEntityId={node.id}
            onLinked={(itemId) => {
              setShowLink(false);
              loadConnections();
              void itemId;
            }}
            onClose={() => setShowLink(false)}
          />
        )}
      </div>

      {/* ── Custom Fields ── */}
      <CustomFieldsSection
        itemId={node.id}
        objectType="roadmap_node"
        metadata={node.metadata}
        onSave={({ metadata }) => updateNode(node.id, { metadata })}
      />

      {/* ── Delete ── */}
      <div className="pt-2 border-t border-[var(--rule)]">
        {confirmDelete ? (
          <>
            <div className="flex items-center gap-2 p-3 mb-2 bg-[var(--coral-dim)] dark:bg-[var(--coral)]/20 border border-[var(--coral)] dark:border-[var(--coral)]">
              <AlertTriangle className="h-4 w-4 text-[var(--coral)] flex-shrink-0" />
              <p className="text-xs text-[var(--coral)] dark:text-[var(--coral)] flex-1 leading-snug">
                Delete this node and its connections?
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleDelete}
                disabled={isDeleting}
                className="flex items-center gap-1.5 flex-1 justify-center px-3 py-2 bg-[var(--coral)] hover:bg-[var(--coral)] disabled:opacity-60 text-white text-xs font-medium transition-colors"
              >
                {isDeleting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                Delete
              </button>
              <button
                onClick={() => setConfirmDelete(false)}
                className="flex-1 px-3 py-2 text-xs text-[var(--text-sec)] dark:text-[var(--text-sec)] border border-[var(--rule)] hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)] transition-colors"
              >
                Cancel
              </button>
            </div>
          </>
        ) : (
          <button
            onClick={() => setConfirmDelete(true)}
            className="flex items-center gap-1.5 text-xs text-[var(--text-ter)] hover:text-[var(--coral)] transition-colors"
          >
            <Trash2 className="h-3.5 w-3.5" />
            Delete node
          </button>
        )}
      </div>
    </div>
  );
}

// ── Sortable layout row ────────────────────────────────────────

interface SortableLayoutRowProps {
  id: string;
  name: string;
  description: string | null;
  onSaveName: (name: string) => Promise<void>;
  onSaveDescription: (desc: string) => Promise<void>;
  onDelete: () => Promise<void>;
}

function SortableLayoutRow({
  id,
  name,
  description,
  onSaveName,
  onSaveDescription,
  onDelete,
}: SortableLayoutRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState(name);
  const [savingName, setSavingName] = useState(false);

  const [expanded, setExpanded] = useState(false);
  const [descDraft, setDescDraft] = useState(description ?? '');
  const descTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  async function commitName() {
    const val = nameDraft.trim();
    if (!val || val === name) { setEditingName(false); setNameDraft(name); return; }
    setSavingName(true);
    await onSaveName(val);
    setSavingName(false);
    setEditingName(false);
  }

  function handleDescChange(val: string) {
    setDescDraft(val);
    if (descTimer.current) clearTimeout(descTimer.current);
    descTimer.current = setTimeout(() => {
      onSaveDescription(val);
    }, 700);
  }

  async function handleDelete() {
    setIsDeleting(true);
    await onDelete();
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="border border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] mb-2 overflow-hidden"
    >
      <div className="flex items-center gap-1 px-2 py-2 group">
        <button
          {...attributes}
          {...listeners}
          className="flex-shrink-0 p-1 cursor-grab active:cursor-grabbing text-[var(--text-ter)] hover:text-[var(--text-ter)] touch-none"
          tabIndex={-1}
        >
          <GripVertical className="h-3.5 w-3.5" />
        </button>

        {editingName ? (
          <div className="flex items-center gap-1 flex-1 min-w-0">
            <input
              autoFocus
              value={nameDraft}
              onChange={(e) => setNameDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') commitName();
                if (e.key === 'Escape') { setNameDraft(name); setEditingName(false); }
              }}
              className="flex-1 min-w-0 px-2 py-0.5 text-sm border border-[var(--slate)] dark:border-[var(--slate)] rounded bg-[var(--bg-raised)] dark:bg-[var(--surface)] text-[var(--text-pri)] dark:text-[var(--text-pri)] focus:outline-none focus:ring-1 focus:ring-purple-500"
            />
            <button
              onClick={commitName}
              disabled={savingName}
              className="flex-shrink-0 p-1 text-[var(--text-ter)] hover:bg-[var(--slate-dim)] dark:hover:bg-[var(--slate)]/20 rounded disabled:opacity-50"
            >
              {savingName ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
            </button>
            <button
              onClick={() => { setNameDraft(name); setEditingName(false); }}
              className="flex-shrink-0 p-1 text-[var(--text-ter)] hover:text-[var(--text-sec)] rounded"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        ) : (
          <>
            <span className="flex-1 min-w-0 text-sm text-[var(--text-pri)] dark:text-[var(--text-pri)] truncate">
              {name}
            </span>
            <button
              onClick={() => { setEditingName(true); setNameDraft(name); }}
              className="flex-shrink-0 opacity-0 group-hover:opacity-100 p-1 text-[var(--text-ter)] hover:text-[var(--text-sec)] rounded transition-opacity"
              title="Rename"
            >
              <Pencil className="h-3 w-3" />
            </button>
            <button
              onClick={() => setExpanded((v) => !v)}
              className="flex-shrink-0 p-1 text-[var(--text-ter)] hover:text-[var(--text-sec)] rounded transition-colors"
              title={expanded ? 'Collapse' : 'Edit description / delete'}
            >
              <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-150 ${expanded ? 'rotate-180' : ''}`} />
            </button>
          </>
        )}
      </div>

      {expanded && !editingName && (
        <div className="px-3 pb-3 pt-1 border-t border-[var(--rule)]">
          <label className="block text-[10px] font-semibold text-[var(--text-ter)] dark:text-[var(--text-ter)] uppercase tracking-wide mb-1">
            Description
          </label>
          <textarea
            value={descDraft}
            onChange={(e) => handleDescChange(e.target.value)}
            rows={2}
            placeholder="Describe what belongs here, what this checkpoint means, etc."
            className="w-full px-2 py-1.5 text-xs border border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg)]/40 text-[var(--text-pri)] dark:text-[var(--text-pri)] placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-purple-500 resize-none"
          />

          {confirmDelete ? (
            <div className="mt-2">
              <p className="text-xs text-[var(--coral)] dark:text-[var(--coral)] mb-1.5 flex items-center gap-1">
                <AlertTriangle className="h-3 w-3 flex-shrink-0" />
                Delete? Nodes will be unassigned.
              </p>
              <div className="flex gap-1.5">
                <button
                  onClick={handleDelete}
                  disabled={isDeleting}
                  className="flex items-center gap-1 px-2.5 py-1 bg-[var(--coral)] hover:bg-[var(--coral)] disabled:opacity-60 text-white text-xs font-medium transition-colors"
                >
                  {isDeleting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
                  Delete
                </button>
                <button
                  onClick={() => setConfirmDelete(false)}
                  className="px-2.5 py-1 text-xs text-[var(--text-sec)] dark:text-[var(--text-sec)] border border-[var(--rule)] hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)] transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setConfirmDelete(true)}
              className="mt-2 flex items-center gap-1 text-xs text-[var(--coral)] hover:text-[var(--coral)] transition-colors"
            >
              <Trash2 className="h-3 w-3" />
              Delete
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ── Layout panel ──────────────────────────────────────────────

interface LayoutPanelProps {
  roadmapId: string;
  swimlanes: RoadmapSwimlane[];
  milestones: RoadmapMilestoneColumn[];
  onClose: () => void;
  isSetupPhase?: boolean;
}

function LayoutPanel({ roadmapId, swimlanes, milestones, onClose, isSetupPhase }: LayoutPanelProps) {
  const {
    createSwimlane,
    updateSwimlane,
    deleteSwimlane,
    createMilestone,
    updateMilestone,
    deleteMilestone,
  } = useRoadmapStore();

  const [newLaneName, setNewLaneName] = useState('');
  const [newMilestoneName, setNewMilestoneName] = useState('');
  const [addingLane, setAddingLane] = useState(false);
  const [addingMilestone, setAddingMilestone] = useState(false);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const sortedLanes = [...swimlanes].sort((a, b) => a.position - b.position);
  const sortedMilestones = [...milestones].sort((a, b) => a.position - b.position);

  const handleLaneDragEnd = useCallback(async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = sortedLanes.findIndex((l) => l.id === active.id);
    const newIdx = sortedLanes.findIndex((l) => l.id === over.id);
    const reordered = arrayMove(sortedLanes, oldIdx, newIdx);
    await Promise.all(reordered.map((lane, i) =>
      lane.position !== i ? updateSwimlane(lane.id, { position: i }) : Promise.resolve({ success: true })
    ));
  }, [sortedLanes, updateSwimlane]);

  const handleMilestoneDragEnd = useCallback(async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = sortedMilestones.findIndex((m) => m.id === active.id);
    const newIdx = sortedMilestones.findIndex((m) => m.id === over.id);
    const reordered = arrayMove(sortedMilestones, oldIdx, newIdx);
    await Promise.all(reordered.map((m, i) =>
      m.position !== i ? updateMilestone(m.id, { position: i }) : Promise.resolve({ success: true })
    ));
  }, [sortedMilestones, updateMilestone]);

  async function handleAddLane() {
    if (!newLaneName.trim()) return;
    setAddingLane(true);
    await createSwimlane(roadmapId, newLaneName.trim());
    setNewLaneName('');
    setAddingLane(false);
  }

  async function handleAddMilestone() {
    if (!newMilestoneName.trim()) return;
    setAddingMilestone(true);
    await createMilestone(roadmapId, newMilestoneName.trim());
    setNewMilestoneName('');
    setAddingMilestone(false);
  }

  return (
    <div className="w-full xl:w-80 flex-shrink-0 border border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] flex flex-col overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--rule)]">
        <h3 className="text-sm font-semibold text-[var(--text-sec)] dark:text-[var(--text-sec)]">Layout</h3>
        <button
          onClick={onClose}
          className="p-1 rounded hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)] text-[var(--text-ter)] hover:text-[var(--text-sec)]"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="flex-1 overflow-auto p-4 space-y-6">

        {/* ── Swimlanes ── */}
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Rows3 className="h-3.5 w-3.5 text-[var(--text-ter)]" />
            <span className="text-xs font-semibold text-[var(--text-ter)] dark:text-[var(--text-ter)] uppercase tracking-wide">
              Swimlanes
            </span>
            <span className="ml-auto text-xs text-[var(--text-ter)]">{swimlanes.length}</span>
          </div>
          <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] mb-3 leading-snug">
            Horizontal rows that group nodes by team, phase, or workstream.
          </p>

          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleLaneDragEnd}>
            <SortableContext items={sortedLanes.map((l) => l.id)} strategy={verticalListSortingStrategy}>
              {sortedLanes.length === 0 ? (
                <p className="text-xs text-[var(--text-ter)] italic py-1 mb-3">No swimlanes yet</p>
              ) : (
                <div className="mb-3">
                  {sortedLanes.map((lane) => (
                    <SortableLayoutRow
                      key={lane.id}
                      id={lane.id}
                      name={lane.name}
                      description={lane.description}
                      onSaveName={(n) => updateSwimlane(lane.id, { name: n }).then(() => {})}
                      onSaveDescription={(d) => updateSwimlane(lane.id, { description: d || null }).then(() => {})}
                      onDelete={() => deleteSwimlane(lane.id).then(() => {})}
                    />
                  ))}
                </div>
              )}
            </SortableContext>
          </DndContext>

          <div className="flex gap-1.5">
            <input
              type="text"
              value={newLaneName}
              onChange={(e) => setNewLaneName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleAddLane(); }}
              placeholder="New lane name…"
              className="flex-1 min-w-0 px-2.5 py-1.5 text-sm border border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] text-[var(--text-pri)] dark:text-[var(--text-pri)] placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-purple-500"
            />
            <button
              onClick={handleAddLane}
              disabled={!newLaneName.trim() || addingLane}
              className="flex-shrink-0 p-2 bg-[var(--slate)] hover:bg-[var(--slate)] disabled:opacity-50 text-white transition-colors"
              title="Add swimlane"
            >
              {addingLane ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
            </button>
          </div>
        </div>

        {/* ── Milestone columns ── */}
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Target className="h-3.5 w-3.5 text-[var(--text-ter)]" />
            <span className="text-xs font-semibold text-[var(--text-ter)] dark:text-[var(--text-ter)] uppercase tracking-wide">
              Milestones
            </span>
            <span className="ml-auto text-xs text-[var(--text-ter)]">{milestones.length}</span>
          </div>
          <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] mb-3 leading-snug">
            Vertical columns that represent time horizons or key checkpoints.
          </p>

          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleMilestoneDragEnd}>
            <SortableContext items={sortedMilestones.map((m) => m.id)} strategy={verticalListSortingStrategy}>
              {sortedMilestones.length === 0 ? (
                <p className="text-xs text-[var(--text-ter)] italic py-1 mb-3">No milestones yet</p>
              ) : (
                <div className="mb-3">
                  {sortedMilestones.map((m) => (
                    <SortableLayoutRow
                      key={m.id}
                      id={m.id}
                      name={m.name}
                      description={m.description}
                      onSaveName={(n) => updateMilestone(m.id, { name: n }).then(() => {})}
                      onSaveDescription={(d) => updateMilestone(m.id, { description: d || null }).then(() => {})}
                      onDelete={() => deleteMilestone(m.id).then(() => {})}
                    />
                  ))}
                </div>
              )}
            </SortableContext>
          </DndContext>

          <div className="flex gap-1.5">
            <input
              type="text"
              value={newMilestoneName}
              onChange={(e) => setNewMilestoneName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleAddMilestone(); }}
              placeholder="New milestone name…"
              className="flex-1 min-w-0 px-2.5 py-1.5 text-sm border border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] text-[var(--text-pri)] dark:text-[var(--text-pri)] placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-purple-500"
            />
            <button
              onClick={handleAddMilestone}
              disabled={!newMilestoneName.trim() || addingMilestone}
              className="flex-shrink-0 p-2 bg-[var(--slate)] hover:bg-[var(--slate)] disabled:opacity-50 text-white transition-colors"
              title="Add milestone"
            >
              {addingMilestone ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
            </button>
          </div>
        </div>

      </div>

      <div className="px-4 py-3 border-t border-[var(--rule)] space-y-3">
        {isSetupPhase ? (
          <button
            onClick={onClose}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-[var(--slate)] hover:bg-[var(--slate)] text-white text-sm font-medium transition-colors"
          >
            Done — Start Adding Conditions
          </button>
        ) : null}
        <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] leading-snug">
          Drag rows to reorder. Expand a row (↓) to add a description or delete it.
          Assign nodes to lanes and milestones via the Grid view or node detail panel.
        </p>
      </div>
    </div>
  );
}

// ── Guide panel ───────────────────────────────────────────────

const GUIDE_TERMS = [
  {
    term: 'Goal Node',
    icon: <Flag className="h-3.5 w-3.5 text-[var(--text-ter)] flex-shrink-0 mt-0.5" />,
    def: 'The desired future state you\'re planning toward. The "end" of the dependency tree.',
  },
  {
    term: 'Prerequisite Condition',
    icon: <Circle className="h-3.5 w-3.5 text-[var(--text-ter)] flex-shrink-0 mt-0.5" />,
    def: 'A state that must be true before a parent condition can be achieved. Add child nodes by asking "What must be true for this to happen?"',
  },
  {
    term: 'Dependency Edge',
    icon: <ArrowRight className="h-3.5 w-3.5 text-[var(--text-ter)] flex-shrink-0 mt-0.5" />,
    def: 'A → B means A must be true for B to become achievable. The system prevents circular dependencies.',
  },
  {
    term: 'Node Status',
    icon: <Clock className="h-3.5 w-3.5 text-[var(--mustard)] flex-shrink-0 mt-0.5" />,
    def: 'Unknown / True / In Progress / Not True. Tracking status turns the tree into a live progress tracker.',
  },
  {
    term: 'Tense',
    icon: <span className="text-[10px] font-mono text-[var(--text-ter)] flex-shrink-0 mt-0.5">T</span>,
    def: 'How the condition is written: Past (delivered), Present (ongoing), or Future (upcoming). Keep tenses consistent within a tree.',
  },
  {
    term: 'Future Point',
    icon: <Calendar className="h-3.5 w-3.5 text-[var(--text-ter)] flex-shrink-0 mt-0.5" />,
    def: 'The time horizon the Roadmap is aimed at — e.g. "18 months," "end of FY26."',
  },
  {
    term: 'Defined Success',
    icon: <Target className="h-3.5 w-3.5 text-[var(--green)] flex-shrink-0 mt-0.5" />,
    def: 'Specific, measurable criteria that confirm the goal node has been achieved.',
  },
  {
    term: 'Swimlane',
    icon: <Layers className="h-3.5 w-3.5 text-[var(--text-ter)] flex-shrink-0 mt-0.5" />,
    def: 'Horizontal grouping (Grid view) by team or workstream. Answers "who owns this condition?"',
  },
  {
    term: 'Milestone Column',
    icon: <span className="text-[10px] font-mono text-[var(--text-ter)] flex-shrink-0 mt-0.5">M</span>,
    def: 'Vertical column (Grid view) representing a time checkpoint or stage gate.',
  },
];

const GUIDE_STEPS = [
  'Set the future point — choose your time horizon first.',
  'Write the goal node — state the desired future condition as if already true.',
  'Ask "What must be true for this?" — add prerequisites as child nodes.',
  'Build forward from your current state to the goal, step by step.',
  'Set status on each node to track real-world progress.',
  '(Optional) Add swimlanes and milestones in Grid view for team / timeline clarity.',
];

function GuidePanel({ onClose }: { onClose: () => void }) {
  const [tab, setTab] = useState<'steps' | 'terms'>('steps');

  return (
    <div className="w-full xl:w-80 flex-shrink-0 border border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] flex flex-col overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--rule)]">
        <div className="flex items-center gap-2">
          <HelpCircle className="h-4 w-4 text-[var(--text-ter)]" />
          <span className="text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">
            Roadmap Guide
          </span>
        </div>
        <button
          onClick={onClose}
          className="p-1 rounded hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)] text-[var(--text-ter)]"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="flex border-b border-[var(--rule)] px-4 pt-2">
        <button
          onClick={() => setTab('steps')}
          className={`pb-2 mr-4 text-xs font-medium border-b-2 transition-colors ${
            tab === 'steps'
              ? 'border-[var(--slate)] text-[var(--text-ter)] dark:text-[var(--text-ter)]'
              : 'border-transparent text-[var(--text-ter)] hover:text-[var(--text-sec)]'
          }`}
        >
          Steps
        </button>
        <button
          onClick={() => setTab('terms')}
          className={`pb-2 text-xs font-medium border-b-2 transition-colors ${
            tab === 'terms'
              ? 'border-[var(--slate)] text-[var(--text-ter)] dark:text-[var(--text-ter)]'
              : 'border-transparent text-[var(--text-ter)] hover:text-[var(--text-sec)]'
          }`}
        >
          Key terms
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-4">
        {tab === 'steps' && (
          <ol className="space-y-3">
            {GUIDE_STEPS.map((s, i) => (
              <li key={i} className="flex gap-2.5">
                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-[var(--slate-dim)] dark:bg-[var(--slate)]/40 text-[var(--text-sec)] text-[10px] font-bold flex items-center justify-center mt-0.5">
                  {i + 1}
                </span>
                <p className="text-xs text-[var(--text-sec)] dark:text-[var(--text-sec)] leading-relaxed">{s}</p>
              </li>
            ))}
          </ol>
        )}

        {tab === 'terms' && (
          <dl className="space-y-4">
            {GUIDE_TERMS.map((t) => (
              <div key={t.term} className="flex gap-2">
                {t.icon}
                <div>
                  <dt className="text-xs font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">{t.term}</dt>
                  <dd className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] mt-0.5 leading-relaxed">{t.def}</dd>
                </div>
              </div>
            ))}
          </dl>
        )}
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────

export default function RoadmapEditor() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const {
    documents,
    nodes,
    edges,
    swimlanes,
    milestones,
    isLoading,
    fetchDocuments,
    fetchRoadmap,
    updateRoadmap,
    createNode,
    deleteRoadmap,
  } = useRoadmapStore();

  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [showLayoutPanel, setShowLayoutPanel] = useState(false);
  const [showGuidePanel, setShowGuidePanel] = useState(false);
  const [layoutSetupDone, setLayoutSetupDone] = useState(false);
  const [showMiroModal, setShowMiroModal] = useState(false);
  const [viewMode, setViewMode] = useState<RoadmapViewMode>('Grid');
  const [gridZoom, setGridZoom] = useState(1.0);

  const ZOOM_STEP = 0.15;
  const zoomIn = () => setGridZoom((z) => Math.min(1.5, parseFloat((z + ZOOM_STEP).toFixed(2))));
  const zoomOut = () => setGridZoom((z) => Math.max(0.4, parseFloat((z - ZOOM_STEP).toFixed(2))));
  const zoomReset = () => setGridZoom(1.0);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const [isSavingTitle, setIsSavingTitle] = useState(false);
  const [isDeletingRoadmap, setIsDeletingRoadmap] = useState(false);

  const doc = documents.find((d) => d.id === id);
  const roadmapNodes = nodes.filter((n) => n.roadmapId === id);
  const roadmapEdges = edges.filter((e) => e.roadmapId === id);
  const roadmapSwimlanes = swimlanes.filter((s) => s.roadmapId === id);
  const roadmapMilestones = milestones.filter((m) => m.roadmapId === id);
  const selectedNode = roadmapNodes.find((n) => n.id === selectedNodeId) ?? null;

  const isSetupPhase = !isLoading && roadmapNodes.length === 0;

  useEffect(() => {
    if (!id) return;
    if (documents.length === 0) fetchDocuments();
    fetchRoadmap(id);
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (doc) {
      setViewMode(doc.viewMode ?? 'Grid');
      setTitleDraft(doc.title);
    }
  }, [doc?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isSetupPhase && !layoutSetupDone) {
      setShowLayoutPanel(true);
      setShowGuidePanel(false);
      setViewMode('Grid');
    }
  }, [isSetupPhase, layoutSetupDone]);

  async function handleTitleSave() {
    if (!id || !titleDraft.trim()) return;
    setIsSavingTitle(true);
    await updateRoadmap(id, { title: titleDraft.trim() });
    setIsSavingTitle(false);
    setIsEditingTitle(false);
  }

  async function handleViewModeChange(mode: RoadmapViewMode) {
    setViewMode(mode);
    if (id) await updateRoadmap(id, { viewMode: mode });
  }

  async function handleAddNode() {
    if (!id) return;
    const { node } = await createNode(id, { text: 'New condition', tense: 'future' });
    if (node) setSelectedNodeId(node.id);
  }

  async function handleMiroImportNodes(importedNodes: { text: string; tense: RoadmapNode['tense'] }[]) {
    if (!id) return;
    for (const n of importedNodes) {
      await createNode(id, { text: n.text, tense: n.tense });
    }
  }

  async function handleDeleteRoadmap() {
    if (!id || !doc) return;
    if (
      !window.confirm(
        `Delete "${doc.title}"? This removes the Roadmap and all its nodes, edges, swimlanes, and milestones. This cannot be undone.`,
      )
    ) {
      return;
    }
    setIsDeletingRoadmap(true);
    const { success, error: delErr } = await deleteRoadmap(id);
    setIsDeletingRoadmap(false);
    if (!success) {
      alert(delErr ?? 'Could not delete Roadmap');
      return;
    }
    navigate('/prioritization/roadmap', { state: location.state });
  }

  if (!id) {
    navigate('/prioritization/roadmap', { state: location.state });
    return null;
  }

  return (
    <div className="flex flex-col">
      {/* Header */}
      <div className="p-4 border border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] mb-4">
        <div className="flex items-center gap-3 mb-1">
          <Link
            to="/prioritization/roadmap"
            state={location.state}
            className="flex items-center gap-1 text-sm text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:text-[var(--text-ter)] dark:hover:text-[var(--text-sec)] transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Roadmaps
          </Link>
          {isSetupPhase && (
            <span className="flex items-center gap-1 text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] font-medium">
              <MapPin className="h-3 w-3" />
              Setup
            </span>
          )}
        </div>

        <div className="flex items-center justify-between gap-4">
          {/* Title — a whole row on a phone, so the buttons wrap below it instead of crushing it */}
          <div className="min-w-0 flex-1 max-md:basis-full">
            {isEditingTitle ? (
              <div className="flex items-center gap-2">
                <input
                  autoFocus
                  value={titleDraft}
                  onChange={(e) => setTitleDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleTitleSave();
                    if (e.key === 'Escape') setIsEditingTitle(false);
                  }}
                  className="flex-1 px-2 py-1 text-xl font-semibold border border-[var(--slate)] focus:outline-none focus:ring-2 focus:ring-purple-500 dark:bg-[var(--surface)] dark:text-[var(--text-pri)]"
                />
                <button
                  onClick={handleTitleSave}
                  disabled={isSavingTitle}
                  className="p-1.5 bg-[var(--slate)] hover:bg-[var(--slate)] disabled:opacity-60 text-white"
                >
                  {isSavingTitle ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                </button>
              </div>
            ) : (
              <button
                onClick={() => setIsEditingTitle(true)}
                className="text-left text-xl font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)] hover:text-[var(--text-ter)] dark:hover:text-[var(--text-ter)] transition-colors truncate max-w-md"
              >
                {doc?.title ?? (isLoading ? 'Loading…' : 'Roadmap')}
              </button>
            )}
          </div>

          {/* View mode tabs */}
          {(!isSetupPhase || layoutSetupDone) && (
            <div className="flex items-center gap-1 p-1 bg-[var(--hover-tint)] flex-shrink-0">
              {VIEW_MODES.map((m) => (
                <button
                  key={m.id}
                  onClick={() => handleViewModeChange(m.id)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium  transition-colors
                    ${viewMode === m.id
                      ? 'bg-[var(--bg-raised)] dark:bg-[var(--surface)] text-[var(--text-pri)] dark:text-[var(--text-pri)] shadow-sm'
                      : 'text-[var(--text-ter)] dark:text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:hover:text-[var(--text-sec)]'
                    }`}
                >
                  {m.icon}
                  {m.label}
                </button>
              ))}
            </div>
          )}

          {/* Layout toggle */}
          <button
            onClick={() => {
              setShowLayoutPanel((v) => !v);
              setShowGuidePanel(false);
              setSelectedNodeId(null);
            }}
            className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium  transition-colors flex-shrink-0
              ${showLayoutPanel
                ? 'bg-[var(--slate-dim)] dark:bg-[var(--slate)]/40 text-[var(--text-sec)]'
                : 'bg-[var(--hover-tint)] text-[var(--text-sec)] dark:text-[var(--text-sec)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-lift)]'
              }`}
            title="Manage swimlanes and milestones"
          >
            <Rows3 className="h-4 w-4" />
            Layout
          </button>

          {(!isSetupPhase || layoutSetupDone) && (
            <StrategyConnectivityHelpButton context="roadmap_editor" className="flex-shrink-0" />
          )}

          {/* Guide toggle */}
          {(!isSetupPhase || layoutSetupDone) && (
            <button
              onClick={() => {
                setShowGuidePanel((v) => !v);
                setShowLayoutPanel(false);
                setSelectedNodeId(null);
              }}
              className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium  transition-colors flex-shrink-0
                ${showGuidePanel
                  ? 'bg-[var(--slate-dim)] dark:bg-[var(--slate)]/40 text-[var(--text-sec)]'
                  : 'bg-[var(--hover-tint)] text-[var(--text-sec)] dark:text-[var(--text-sec)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-lift)]'
                }`}
              title="Roadmap guide and key terms"
            >
              <HelpCircle className="h-4 w-4" />
              Guide
            </button>
          )}

          {(!isSetupPhase || layoutSetupDone) && (
            <Link
              to={`/prioritization/roadmap/${id}/print`}
              state={location.state}
              className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium transition-colors flex-shrink-0 bg-[var(--hover-tint)] text-[var(--text-sec)] dark:text-[var(--text-sec)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-lift)]"
              title="Open paper-sized print preview for this roadmap"
            >
              <Printer className="h-4 w-4" />
              Print
            </Link>
          )}

          <button
            type="button"
            onClick={handleDeleteRoadmap}
            disabled={isDeletingRoadmap || isLoading}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium transition-colors flex-shrink-0 bg-[var(--hover-tint)] text-[var(--coral)] dark:text-[var(--coral)] hover:bg-[var(--coral-dim)] dark:hover:bg-[var(--coral)]/20 disabled:opacity-50"
            title="Delete this Roadmap"
          >
            {isDeletingRoadmap ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
            Delete
          </button>

          {/* Miro export/import */}
          {(!isSetupPhase || layoutSetupDone) && (
            <button
              onClick={() => {
                setShowMiroModal(true);
                setShowLayoutPanel(false);
                setShowGuidePanel(false);
              }}
              className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium transition-colors flex-shrink-0 bg-[var(--hover-tint)] text-[var(--text-sec)] dark:text-[var(--text-sec)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-lift)]"
              title="Send to Miro or import from Miro"
            >
              <Share2 className="h-4 w-4" />
              Miro
            </button>
          )}

          {/* Add node */}
          {(!isSetupPhase || layoutSetupDone) && (
            <button
              onClick={handleAddNode}
              className="flex items-center gap-1.5 px-3 py-2 bg-[var(--slate)] hover:bg-[var(--slate)] text-white text-sm font-medium transition-colors flex-shrink-0"
            >
              <Plus className="h-4 w-4" />
              Add Node
            </button>
          )}
        </div>
      </div>

      {/* Sync panel */}
      {(!isSetupPhase || layoutSetupDone) && (
        <div className="mb-4">
          <EntitySyncPanel
            entityType="roadmap_nodes"
            data={roadmapNodes.map((n) => ({
              text: n.text,
              tense: n.tense,
              is_goal: n.isGoal ? 'true' : 'false',
              status: n.status,
              tags: (n.tags ?? []).join(', '),
              position: n.position,
            }))}
            filename="roadmap-nodes"
            onImport={async (rows) => {
              if (!id) return [];
              // Return created identities so the panel registers external identity
              // + a change event for connector-sourced rows (set 6 convergence).
              const refs: { externalId: string; internalId: string; externalUrl?: string; label?: string }[] = [];
              for (const row of rows) {
                const { node } = await createNode(id, {
                  text: String(row.text ?? ''),
                  tense: (['past', 'present', 'future'].includes(String(row.tense))
                    ? String(row.tense)
                    : 'future') as RoadmapNode['tense'],
                  isGoal: String(row.is_goal).toLowerCase() === 'true',
                });
                if (node?.id && row.external_id) {
                  refs.push({
                    externalId: String(row.external_id),
                    internalId: node.id,
                    ...(row.external_url ? { externalUrl: String(row.external_url) } : {}),
                    label: String(row.text ?? ''),
                  });
                }
              }
              return refs;
            }}
          />
        </div>
      )}

      {/* Body */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20 text-[var(--text-ter)]">
          <Loader2 className="h-6 w-6 animate-spin mr-2" />
          Loading…
        </div>
      ) : (
        <div className="flex flex-col xl:flex-row gap-4 items-start">
          {/* Main view */}
          <div className="flex-1 min-w-0">
            {isSetupPhase && !layoutSetupDone ? (
              <div className="border border-[var(--slate)] dark:border-[var(--slate)]/60 bg-[var(--slate-dim)] dark:bg-[var(--slate)]/10 p-8 text-center">
                <div className="flex items-center justify-center gap-2 mb-4">
                  <div className="h-10 w-10 rounded-full bg-[var(--slate-dim)] dark:bg-[var(--slate)]/40 flex items-center justify-center">
                    <Rows3 className="h-5 w-5 text-[var(--text-ter)] dark:text-[var(--text-ter)]" />
                  </div>
                </div>
                <h3 className="text-base font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)] mb-1">
                  Set up your structure first
                </h3>
                <p className="text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)] mb-5 max-w-sm mx-auto leading-relaxed">
                  Use the <strong>Layout</strong> panel on the right to define your
                  swimlanes and milestones. When you're ready, click{' '}
                  <strong>Done — Start Adding Conditions</strong> at the bottom of
                  the panel to begin.
                </p>
                <div className="flex items-start justify-center gap-8 text-sm text-[var(--text-sec)]">
                  <div className="flex flex-col items-center gap-1.5 max-w-[140px]">
                    <div className="h-8 w-8 rounded-full bg-[var(--slate-dim)] dark:bg-[var(--slate)]/40 flex items-center justify-center text-xs font-bold text-[var(--text-sec)]">1</div>
                    <span className="font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)]">Swimlanes</span>
                    <span className="text-xs leading-snug text-center">Horizontal rows — one per team, role, or workstream</span>
                  </div>
                  <div className="flex flex-col items-center gap-1.5 max-w-[140px]">
                    <div className="h-8 w-8 rounded-full bg-[var(--slate-dim)] dark:bg-[var(--slate)]/40 flex items-center justify-center text-xs font-bold text-[var(--text-sec)]">2</div>
                    <span className="font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)]">Milestones</span>
                    <span className="text-xs leading-snug text-center">Vertical columns — one per checkpoint or time horizon</span>
                  </div>
                  <div className="flex flex-col items-center gap-1.5 max-w-[140px]">
                    <div className="h-8 w-8 rounded-full bg-[var(--slate-dim)] dark:bg-[var(--slate)]/40 flex items-center justify-center text-xs font-bold text-[var(--text-sec)]">3</div>
                    <span className="font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)]">Add conditions</span>
                    <span className="text-xs leading-snug text-center">Click + in any cell to add conditions that must be true</span>
                  </div>
                </div>
              </div>
            ) : (
              <>
                {(viewMode === 'Tree' || viewMode === 'Split') && (
                  <TreeView
                    roadmapId={id}
                    nodes={roadmapNodes}
                    edges={roadmapEdges}
                    onNodeSelect={setSelectedNodeId}
                    selectedNodeId={selectedNodeId}
                  />
                )}
                {viewMode === 'Grid' && (
                  <GridView
                    roadmapId={id}
                    nodes={roadmapNodes}
                    swimlanes={roadmapSwimlanes}
                    milestones={roadmapMilestones}
                    onNodeSelect={setSelectedNodeId}
                    selectedNodeId={selectedNodeId}
                    zoom={gridZoom}
                    onZoomIn={zoomIn}
                    onZoomOut={zoomOut}
                    onZoomReset={zoomReset}
                    goal={doc ? {
                      title: doc.title,
                      objective: doc.objective || undefined,
                      futurePoint: doc.futurePoint || undefined,
                      definedSuccess: doc.definedSuccess,
                    } : undefined}
                  />
                )}
              </>
            )}
          </div>

          {/* Right panel: node detail > layout > guide */}
          {selectedNode ? (
            <NodeDetailPanel
              node={selectedNode}
              onClose={() => setSelectedNodeId(null)}
            />
          ) : (showLayoutPanel || (isSetupPhase && !layoutSetupDone)) ? (
            <LayoutPanel
              roadmapId={id}
              swimlanes={roadmapSwimlanes}
              milestones={roadmapMilestones}
              isSetupPhase={isSetupPhase}
              onClose={() => {
                setShowLayoutPanel(false);
                if (isSetupPhase) setLayoutSetupDone(true);
              }}
            />
          ) : showGuidePanel ? (
            <GuidePanel onClose={() => setShowGuidePanel(false)} />
          ) : null}
        </div>
      )}

      {/* Miro integration modal */}
      {showMiroModal && doc && (
        <RoadmapMiroModal
          doc={doc}
          nodes={roadmapNodes}
          swimlanes={roadmapSwimlanes}
          milestones={roadmapMilestones}
          onClose={() => setShowMiroModal(false)}
          onImportNodes={handleMiroImportNodes}
        />
      )}
    </div>
  );
}
